import React, { useMemo, useState } from 'react'
import { Badge, Button, Card, CardBody, Col, Input, Label, Row, Spinner } from 'reactstrap'
import PageWrapper from '../../../components/Common/PageWrapper'
import TableContainer from '../../../components/Common/TableContainer'
import { useActivityLog, useActivityLogs } from '../../../hooks/useActivityLog'
import { useListParams } from '../../../hooks/useListParams'
import { formatReadableDate } from '../../../utils/formatDate'
import ActivityLogView from './ActivityLogView'

const ACTION_OPTIONS = [
  'create',
  'update',
  'delete',
  'restore',
  'login',
  'logout',
  'grant_access',
  'revoke_access',
  'update_roles',
  'reset_password',
  'impersonate',
]

const ACTION_STYLES = {
  create: { color: 'success', icon: 'mdi-plus-circle' },
  update: { color: 'info', icon: 'mdi-pencil-circle' },
  delete: { color: 'danger', icon: 'mdi-delete-circle' },
  restore: { color: 'warning', icon: 'mdi-restore' },
  login: { color: 'primary', icon: 'mdi-login' },
  logout: { color: 'secondary', icon: 'mdi-logout' },
  grant_access: { color: 'success', icon: 'mdi-account-key' },
  revoke_access: { color: 'danger', icon: 'mdi-account-off' },
  update_roles: { color: 'info', icon: 'mdi-shield-account' },
  reset_password: { color: 'warning', icon: 'mdi-lock-reset' },
  impersonate: { color: 'dark', icon: 'mdi-account-switch' },
}

const ActivityLogPage = () => {
  document.title = 'Activity Log - Aurobil'

  const [filters, setFilters] = useState({
    module: '',
    action: '',
    causer_user_id: '',
  })
  const [selectedId, setSelectedId] = useState(null)
  const [viewOpen, setViewOpen] = useState(false)

  const { params, setPage, resetParams } = useListParams({
    page: 1,
    search: '',
  })

  const queryParams = useMemo(
    () => ({
      page: params.page || 1,
      per_page: params.per_page || 50,
      search: params.search,
      ...filters,
    }),
    [params, filters]
  )

  const { data, isLoading, isRefetching, refetch } = useActivityLogs(queryParams)
  const { data: detailData, isLoading: isDetailLoading } = useActivityLog(selectedId, !!selectedId)

  const paginationInfo = data?.data || {}
  const logs = data?.data?.data || []

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const resetAll = () => {
    setFilters({ module: '', action: '', causer_user_id: '' })
    resetParams()
  }

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        cell: ({ row }) => <span className="font-monospace fw-semibold">#{row.original.id}</span>,
      },
      {
        header: 'Module',
        cell: ({ row }) => <Badge color="light" className="text-dark text-uppercase">{row.original.module}</Badge>,
      },
      {
        header: 'Action',
        cell: ({ row }) => {
          const action = row.original.action
          const config = ACTION_STYLES[action] || { color: 'secondary', icon: 'mdi-history' }

          return (
            <Badge color={config.color} className="d-flex align-items-center gap-1 w-fit">
              <i className={`mdi ${config.icon}`} />
              <span>{action}</span>
            </Badge>
          )
        },
      },
      {
        header: 'Subject',
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.subject_label || 'N/A'}</div>
            <div className="small text-muted">
              {row.original.subject_type?.split('\\').pop() || 'Unknown'}
              {row.original.subject_id ? ` #${row.original.subject_id}` : ''}
            </div>
          </div>
        ),
      },
      {
        header: 'User',
        cell: ({ row }) => (
          <div className="small">
            {row.original.causer_user_display || row.original.causer_user_snapshot?.label || row.original.user?.name || row.original.user?.username || 'System'}
          </div>
        ),
      },
      {
        header: 'Showroom',
        cell: ({ row }) => (
          <div className="small">
            {row.original.showroom_display || row.original.showroom_snapshot?.label || row.original.showroom?.short_name || row.original.showroom?.name || 'N/A'}
          </div>
        ),
      },
      {
        header: 'Time',
        cell: ({ row }) => (
          <small className="text-muted">{formatReadableDate(row.original.created_at)}</small>
        ),
      },
      {
        header: 'Action',
        cell: ({ row }) => (
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setSelectedId(row.original.id)
              setViewOpen(true)
            }}
          >
            <i className="mdi mdi-eye-outline" />
          </Button>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Activity Log" breadcrumbItem="Activity Log">
      <Card>
        <CardBody>
          <div className="mb-4 p-3 bg-light border rounded">
            <h6 className="mb-2 fw-semibold">
              <i className="mdi mdi-history me-2 text-primary" />
              Central User Activity Tracking
            </h6>
            <p className="small text-muted mb-0">
              Tenant-scoped activity history for CRUD events, access-control events, and authentication events across modules.
            </p>
          </div>

          <Row className="g-2 align-items-end mb-3">
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Module</Label>
              <Input
                type="text"
                bsSize="sm"
                placeholder="stock, customer, account"
                value={filters.module}
                onChange={e => handleFilterChange('module', e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">Action</Label>
              <Input
                type="select"
                bsSize="sm"
                value={filters.action}
                onChange={e => handleFilterChange('action', e.target.value)}
              >
                <option value="">All Actions</option>
                {ACTION_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Input>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Label className="mb-1 small fw-semibold">User ID</Label>
              <Input
                type="text"
                bsSize="sm"
                placeholder="Filter by user id"
                value={filters.causer_user_id}
                onChange={e => handleFilterChange('causer_user_id', e.target.value)}
              />
            </Col>
            <Col xs="auto">
              <Button color="secondary" size="sm" outline onClick={resetAll}>
                <i className="mdi mdi-refresh me-1" />
                Reset
              </Button>
            </Col>
          </Row>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner size="sm" color="primary" />
              <p className="text-muted small mt-2">Loading activity logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="mdi mdi-inbox-outline display-4 opacity-50" />
              <p className="mt-2">No activity logs found</p>
            </div>
          ) : (
            <>
              <TableContainer
                columns={columns}
                data={logs}
                isPagination={!!paginationInfo}
                paginationInfo={paginationInfo}
                onPageChange={setPage}
                loading={isLoading || isRefetching}
              />

              <div className="small text-muted mt-3 d-flex justify-content-between align-items-center">
                <span>
                  Showing {logs.length} of {paginationInfo.total} activity logs
                </span>
                <Button color="primary" size="sm" outline onClick={() => refetch()} disabled={isRefetching}>
                  <i className="mdi mdi-refresh me-1" />
                  Refresh
                </Button>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <ActivityLogView
        isOpen={viewOpen}
        toggle={() => {
          setViewOpen(false)
          setSelectedId(null)
        }}
        activityLog={detailData?.data}
        isLoading={isDetailLoading}
      />
    </PageWrapper>
  )
}

export default ActivityLogPage
